import type { Dictionary } from '../i18n';
import {
  ACCEPTED_MIME_TYPES,
  MAX_ADDITIONAL_FILES,
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_MB,
  type OwnerContact,
  type ParcelDetails,
  type ParcelSelection,
  type SubmissionFiles,
} from '../domain/types';

/**
 * Client-side validation.
 *
 * This exists to give fast, kind feedback — never as a security boundary. The
 * server re-validates everything independently and is the only check that counts.
 *
 * Errors are keyed by field name so the form can render them inline and move
 * focus to the first offender.
 */

export type Errors = Record<string, string>;

/** Field order drives which error gets focus, so it must match the visual order. */
export const STEP2_FIELD_ORDER = [
  'areaSqm',
  'statutTeren',
  'cut',
  'pot',
  'zonare',
  'zonareAlt',
  'stareTeren',
  'situatieJuridica',
  'certificate',
  'additional',
] as const;

export const STEP3_FIELD_ORDER = ['name', 'email', 'phone', 'consent'] as const;

/**
 * Parse a number the way a Romanian actually writes one.
 *
 * Romanian notation is the inverse of English: "." groups thousands and ","
 * is the decimal separator. "4.500" means four and a half thousand, not four
 * and a half. A naive `parseFloat` reads it as 4.5 and silently submits a
 * 4,500 m² plot as 4.5 m² — so the grouping cases below are load-bearing, not
 * politeness.
 *
 * Rules, in order:
 *  - Both separators present: whichever comes last is the decimal one.
 *  - Comma only: it is the decimal separator ("1,5" -> 1.5).
 *  - Dots only: ambiguous. Treat as grouping only when the string is exactly a
 *    grouped integer ("4.500", "1.234.567"); otherwise it is a decimal point,
 *    so "1.5" still parses as 1.5 for CUT.
 */
export function parseDecimal(raw: string): number | null {
  let s = raw.trim();
  if (s === '') return null;

  // Spaces and non-breaking spaces are also used for grouping.
  s = s.replace(/[\s ]/g, '');

  const lastComma = s.lastIndexOf(',');
  const lastDot = s.lastIndexOf('.');

  if (lastComma !== -1 && lastDot !== -1) {
    if (lastComma > lastDot) s = s.replace(/\./g, '').replace(',', '.');
    else s = s.replace(/,/g, '');
  } else if (lastComma !== -1) {
    s = s.replace(',', '.');
  } else if (lastDot !== -1 && /^\d{1,3}(\.\d{3})+$/.test(s)) {
    s = s.replace(/\./g, '');
  }

  const value = Number.parseFloat(s);
  return Number.isFinite(value) ? value : null;
}

/** A parcel larger than this is almost certainly a mis-drawn polygon. */
const MAX_AREA_SQM = 100_000_000; // 100 km²
const MAX_CUT = 20;

export function isAcceptedFile(file: File): boolean {
  // A browser can report an empty or wrong type; the extension is the fallback
  // hint here. The server sniffs magic bytes and is the real gate.
  if ((ACCEPTED_MIME_TYPES as readonly string[]).includes(file.type)) return true;
  return /\.(pdf|jpe?g|png)$/i.test(file.name);
}

export function validateFile(file: File, t: Dictionary): string | null {
  if (!isAcceptedFile(file)) return t.validation.fileType;
  if (file.size > MAX_FILE_SIZE_BYTES) return t.validation.fileTooLarge(MAX_FILE_SIZE_MB);
  return null;
}

export function validateStep1(selection: ParcelSelection, t: Dictionary): Errors {
  const errors: Errors = {};
  if (selection.method === 'draw') {
    if (!selection.geojson || !selection.areaSqm || selection.areaSqm <= 0) {
      errors['parcel'] = t.step1.gateDraw;
    }
    return errors;
  }

  if (selection.areaSqm === null || selection.areaSqm <= 0) errors['manualArea'] = t.validation.areaInvalid;
  if (selection.areaSqm !== null && selection.areaSqm > MAX_AREA_SQM)
    errors['manualArea'] = t.validation.areaTooLarge;
  if (selection.localityGuess.trim() === '') errors['manualLocality'] = t.validation.localitateRequired;
  return errors;
}

/**
 * Step 2 is entirely optional.
 *
 * Nothing here is required — an owner may pass through without typing anything,
 * and the operator chases the detail later. So every rule below is conditional
 * on the field having a value: we validate *shape*, never *presence*. The only
 * thing that can fail is something the owner actually filled in wrongly (a CUT
 * of 99, a .exe renamed to .pdf), because staying silent about that would mean
 * storing a number the analyst then has to distrust.
 *
 * The missing certificate is handled separately, as a non-blocking notice on
 * step 3 — see `isCertificateMissing`.
 */
export function validateStep2(details: ParcelDetails, files: SubmissionFiles, t: Dictionary): Errors {
  const errors: Errors = {};

  if (details.areaSqm.trim() !== '') {
    const area = parseDecimal(details.areaSqm);
    if (area === null) errors['areaSqm'] = t.validation.areaInvalid;
    else if (area <= 0) errors['areaSqm'] = t.validation.areaInvalid;
    else if (area > MAX_AREA_SQM) errors['areaSqm'] = t.validation.areaTooLarge;
  }

  if (details.cut.trim() !== '') {
    const cut = parseDecimal(details.cut);
    if (cut === null || cut < 0 || cut > MAX_CUT) errors['cut'] = t.validation.cutInvalid;
  }

  if (details.pot.trim() !== '') {
    const pot = parseDecimal(details.pot);
    if (pot === null || pot < 0 || pot > 100) errors['pot'] = t.validation.potInvalid;
  }

  // "Altul" is the one conditional requirement left: choosing it and then not
  // saying what it is leaves a value that means nothing to the analyst.
  if (details.zonare === 'altul' && details.zonareAlt.trim() === '') {
    errors['zonareAlt'] = t.validation.zonareAltRequired;
  }

  if (files.certificate) {
    const fileError = validateFile(files.certificate.file, t);
    if (fileError) errors['certificate'] = fileError;
  }

  if (files.additional.length > MAX_ADDITIONAL_FILES) {
    errors['additional'] = t.validation.tooManyFiles(MAX_ADDITIONAL_FILES);
  } else {
    for (const entry of files.additional) {
      const fileError = validateFile(entry.file, t);
      if (fileError) {
        errors['additional'] = fileError;
        break;
      }
    }
  }

  return errors;
}

/** Drives the non-blocking notice on step 3. Never blocks submission. */
export function isCertificateMissing(files: SubmissionFiles): boolean {
  return files.certificate === null;
}

/**
 * Deliberately permissive. An over-strict pattern rejects real addresses, and
 * the only way to truly validate an address is to send mail to it — which this
 * form does. The server applies the same shape.
 */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * Romanian numbers may be written 0722 123 456, +40 722 123 456, 0040722123456.
 * Strip formatting and require a plausible digit count rather than a strict shape.
 */
export function isPlausiblePhone(raw: string): boolean {
  const digits = raw.replace(/[^\d]/g, '');
  return digits.length >= 9 && digits.length <= 15;
}

export function validateStep3(contact: OwnerContact, t: Dictionary): Errors {
  const errors: Errors = {};

  if (contact.name.trim() === '') errors['name'] = t.validation.nameRequired;

  if (contact.email.trim() === '') errors['email'] = t.validation.emailRequired;
  else if (!EMAIL_PATTERN.test(contact.email.trim())) errors['email'] = t.validation.emailInvalid;

  if (contact.phone.trim() === '') errors['phone'] = t.validation.phoneRequired;
  else if (!isPlausiblePhone(contact.phone)) errors['phone'] = t.validation.phoneInvalid;

  if (!contact.consent) errors['consent'] = t.validation.consentRequired;

  return errors;
}

/** Move focus to the first field in `order` that has an error. */
export function focusFirstError(errors: Errors, order: readonly string[]): void {
  const first = order.find((field) => errors[field]);
  if (!first) return;
  // Rendered by the form as data-field on the control itself.
  const el = document.querySelector<HTMLElement>(`[data-field="${first}"]`);
  if (!el) return;
  el.focus({ preventScroll: true });
  el.scrollIntoView({ block: 'center', behavior: 'smooth' });
}
